import { useEffect, useMemo, useState } from 'react';
import { Activity, CalendarCheck, Dumbbell, Moon, Salad, Sparkles, Trophy } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { STATUS_TEXT, StatusBadge, tagDe } from '@/components/CoachKundeninfo';
import { TrainerFilter } from '@/pages/CoachHeute';

/* Portfolio-Cockpit /coach/fortschritt (Coach-Cockpit W2.3, 29.10.2026): Durchschnitte über alle sichtbaren Kunden —
   fehlende Werte zählen nicht mit (der Server rechnet, `coach/heute.py` portfolio). Zeitraum 7/30/90 Tage/Gesamt,
   Statusverteilung, „Heute handeln“, „Starke Wochen“ und eine filterbare Tabelle. */

const ZEITRAUM = [['7', '7 Tage'], ['30', '30 Tage'], ['90', '90 Tage'], ['gesamt', 'Gesamt']];
const BEREICH_TEXT = { training: 'Training', ernaehrung: 'Ernährung', pt: 'PT', phase: 'Phase' };
const ZIEL_TEXT = { abnehmen: 'Abnehmen', halten: 'Halten', aufbauen: 'Aufbauen' };
const CHECKIN = { eingereicht: 'eingereicht', ausgewertet: 'ausgewertet', offen: 'offen', ueberfaellig: 'überfällig', urlaub: 'Urlaub' };
const KARTE = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';
const chip = (an) => `inline-flex min-h-[44px] items-center rounded-full border px-[11px] py-[5px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground/80 hover:text-foreground'}`;
const KNOPF = 'inline-flex min-h-[44px] shrink-0 items-center rounded-[10px] border border-border bg-muted px-4 text-sm font-bold hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const STATUS_FLEX = { auf_kurs: 'bg-[#3f9a68]', stabil: 'bg-[#6d6d6d]', beobachten: 'bg-[#d9a640]', kritisch: 'bg-[#e0603f]', daten_fehlen: 'bg-[#3a3a3a]' };
const zahl = (v, einheit = '') => (v == null ? '–' : `${String(v).replace('.', ',')}${einheit}`);

function Kachel({ label, wert, einheit, sub, icon: Icon, testId, akzent }) {
  return (
    <div className={KARTE} data-testid={testId}>
      <div className="flex items-center justify-between text-[13px] text-muted-foreground"><span>{label}</span><Icon className="h-4 w-4" aria-hidden="true" /></div>
      <p className={`coach-zahl mt-1 text-[28px] font-extrabold leading-[1.1] tabular-nums ${akzent && wert ? 'text-marke' : ''}`}>{wert == null ? '–' : Math.round(wert)}{wert != null && einheit && <span className="ml-1 text-sm font-semibold text-muted-foreground">{einheit}</span>}</p>
      {sub && <p className="text-[13px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

const Auswahl = ({ label, wert, onWahl, optionen, testId }) => (
  <label className="flex items-center gap-2 text-sm font-semibold">
    <span className="text-muted-foreground">{label}</span>
    <select value={wert} onChange={(e) => onWahl(e.target.value)} data-testid={testId}
      className="min-h-[44px] rounded-[10px] border border-border bg-muted px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <option value="">Alle</option>
      {optionen.map(([id, t]) => <option key={id} value={id}>{t}</option>)}
    </select>
  </label>
);

export default function CoachPortfolio({ coach, gehe }) {
  const [zeitraum, setZeitraum] = useState('30');
  const [trainer, setTrainer] = useState(null);
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [ziel, setZiel] = useState('');
  const [status, setStatus] = useState('');
  const [bereich, setBereich] = useState('');
  useEffect(() => {
    let weg = false;
    setD(null);
    coach.get('/portfolio', { params: { zeitraum, ...(trainer ? { trainer } : {}) } })
      .then((r) => { if (!weg) { setD(r.data); setFehler(''); } })
      .catch((e) => { if (!weg) setFehler(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Portfolio nicht geladen'); });
    return () => { weg = true; };
  }, [coach, zeitraum, trainer]);
  const kunden = useMemo(() => (d?.kunden || []).filter((k) => (!ziel || k.ziel === ziel) && (!status || k.status === status)
    && (!bereich || k.bereiche.includes(bereich))), [d, ziel, status, bereich]);

  const akte = (id, reiter) => gehe({ ansicht: 'kunde', kunde: id, ...(reiter ? { reiter } : {}) });
  return (
    <div className="space-y-[18px]" data-testid="coach-portfolio">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Zeitraum">
          {ZEITRAUM.map(([id, t]) => <button key={id} type="button" className={chip(zeitraum === id)} aria-pressed={zeitraum === id} onClick={() => setZeitraum(id)} data-testid={`portfolio-zeitraum-${id}`}>{t}</button>)}
        </div>
        <TrainerFilter liste={d?.trainer_liste} wert={trainer} onWahl={setTrainer} />
      </div>
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      {!d && !fehler && <div className="space-y-3" aria-busy="true"><Skeleton className="h-24 rounded-[14px]" /><Skeleton className="h-64 rounded-[14px]" /></div>}
      {d && (<>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
          <Kachel label="Ø Momentum" wert={d.kacheln.momentum} icon={Activity} sub="von 100" testId="portfolio-momentum" />
          <Kachel label="Ø Training" wert={d.kacheln.training} einheit="%" icon={Dumbbell} sub="Plan gegen Ist" testId="portfolio-training" />
          <Kachel label="Ø Ernährung" wert={d.kacheln.ernaehrung} einheit="%" icon={Salad} sub="Tage im Ziel" testId="portfolio-ernaehrung" />
          <Kachel label="Ø Erholung" wert={d.kacheln.erholung} icon={Moon} sub="Schlaf · Garmin" testId="portfolio-erholung" />
          <Kachel label="Check-in fällig" wert={d.kacheln.checkin_faellig} icon={CalendarCheck} sub="überschritten" akzent testId="portfolio-checkin" />
        </div>

        <section className={KARTE} aria-labelledby="pf-verteilung">
          <h3 id="pf-verteilung" className="text-base font-bold">Verteilung</h3>
          {(() => {
            const summe = Object.values(d.verteilung).reduce((a, b) => a + b, 0) || 1;
            return (<>
              <div className="mt-3 flex h-3.5 gap-0.5 overflow-hidden rounded-full" aria-hidden="true">
                {Object.entries(d.verteilung).map(([s, n]) => n ? <div key={s} className={STATUS_FLEX[s]} style={{ width: `${(100 * n) / summe}%` }} /> : null)}
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                {Object.entries(d.verteilung).map(([s, n]) => (
                  <li key={s} className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_FLEX[s]}`} aria-hidden="true" />{STATUS_TEXT[s]} <b className="tabular-nums">{n}</b></li>
                ))}
              </ul>
            </>);
          })()}
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className={KARTE} aria-labelledby="pf-handeln">
            <h3 id="pf-handeln" className="flex items-center gap-2 text-base font-bold"><Sparkles className="h-4 w-4 text-marke" aria-hidden="true" /> Heute handeln <span className="ml-auto text-[13px] font-semibold text-muted-foreground">nach Dringlichkeit</span></h3>
            {!d.heute_handeln.length ? <p className="py-6 text-sm text-muted-foreground">Niemand kritisch oder unter Beobachtung.</p> : (
              <ul className="mt-2 divide-y divide-border">
                {d.heute_handeln.map((h) => (
                  <li key={h.lead_id} className="flex items-center justify-between gap-3 py-2">
                    <button type="button" onClick={() => akte(h.lead_id, 'fortschritt')} className="min-h-[44px] min-w-0 flex-1 text-left hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <span className="block break-words text-sm"><b>{h.name}</b> <StatusBadge status={h.status} /> <span className="text-xs tabular-nums text-muted-foreground">{h.momentum ?? ''}</span></span>
                      {h.text && <span className="block break-words text-[13px] text-muted-foreground">{h.text}</span>}
                    </button>
                    <button type="button" className={KNOPF} onClick={() => akte(h.lead_id, 'chat')} aria-label={`Nachricht an ${h.name}`}>Nachricht</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className={KARTE} aria-labelledby="pf-stark">
            <h3 id="pf-stark" className="flex items-center gap-2 text-base font-bold"><Trophy className="h-4 w-4 text-marke" aria-hidden="true" /> Starke Wochen</h3>
            {!d.starke_wochen.length ? <p className="py-6 text-sm text-muted-foreground">Noch keine Meilensteine in diesem Zeitraum.</p> : (
              <ul className="mt-2 divide-y divide-border">
                {d.starke_wochen.map((m) => (
                  <li key={`${m.lead_id}:${m.typ}:${m.text}`}><button type="button" onClick={() => akte(m.lead_id, 'fortschritt')} className="flex min-h-[44px] w-full items-center justify-between gap-3 py-2 text-left hover:bg-muted/40">
                    <span className="min-w-0"><span className="block text-sm font-bold">{m.name}</span><span className="block break-words text-xs text-muted-foreground">{m.text}</span></span>
                    <span className="shrink-0 text-xs text-muted-foreground">{tagDe(m.am)}</span>
                  </button></li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className={KARTE} aria-labelledby="pf-tabelle">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <h3 id="pf-tabelle" className="text-base font-bold">Alle Kunden <span className="text-muted-foreground">({kunden.length})</span></h3>
            <div className="flex flex-wrap gap-2">
              <Auswahl label="Ziel" wert={ziel} onWahl={setZiel} optionen={d.ziele.map((z) => [z, ZIEL_TEXT[z] || z])} testId="portfolio-ziel" />
              <Auswahl label="Status" wert={status} onWahl={setStatus} optionen={Object.entries(STATUS_TEXT)} testId="portfolio-status" />
              <Auswahl label="Bereich" wert={bereich} onWahl={setBereich} optionen={Object.entries(BEREICH_TEXT)} testId="portfolio-bereich" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm" data-testid="portfolio-tabelle">
              <thead><tr className="border-b border-border text-left text-[13px] font-extrabold text-muted-foreground">
                <th className="py-2 pr-3">Kunde</th><th className="py-2 pr-3">Momentum</th><th className="py-2 pr-3">Gewicht Δ</th><th className="py-2 pr-3">Training</th>
                <th className="py-2 pr-3">Ernährung</th><th className="py-2 pr-3">Erholung</th><th className="py-2 pr-3">Letzter Check-in</th><th className="py-2">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {kunden.map((k) => (
                  <tr key={k.lead_id} className="cursor-pointer hover:bg-muted/40" onClick={() => akte(k.lead_id, 'fortschritt')}>
                    <td className="py-2.5 pr-3"><button type="button" className="text-left font-bold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={(e) => { e.stopPropagation(); akte(k.lead_id, 'fortschritt'); }}>{k.name}</button>
                      <span className="block text-xs text-muted-foreground">{[k.bereiche.map((b) => BEREICH_TEXT[b]).join(' · '), k.trainer.join(', ')].filter(Boolean).join(' — ')}</span></td>
                    <td className="py-2.5 pr-3 tabular-nums">{zahl(k.momentum)}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{k.gewicht?.delta != null ? `${k.gewicht.delta > 0 ? '+' : ''}${zahl(k.gewicht.delta)} kg` : '–'}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{zahl(k.training == null ? null : Math.round(k.training), ' %')}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{zahl(k.ernaehrung == null ? null : Math.round(k.ernaehrung), ' %')}</td>
                    <td className="py-2.5 pr-3 tabular-nums">{zahl(k.erholung == null ? null : Math.round(k.erholung))}</td>
                    <td className="py-2.5 pr-3">{k.letzter_checkin ? `${tagDe(k.letzter_checkin.faellig)} · ${CHECKIN[k.letzter_checkin.stand] || k.letzter_checkin.stand}` : '–'}</td>
                    <td className="py-2.5"><StatusBadge status={k.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>)}
    </div>
  );
}
